import { useForm } from '@folklore/forms';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { AuthResponse, useAuthLogin, useAuthSendToken } from '../../hooks/useAuth';

import FormButton from '../buttons/FormButton';
import ContactField from '../fields/ContactField';
import FieldArrow from '../icons/FieldArrowIcon';
import LoadingIcon from '../icons/LoadingIcon';
import FormControl from './FormControl';
import VerifyToken from './VerifyTokenForm';

import styles from '../../styles/forms/login-form.module.css';

export interface LoginFormProps {
    onComplete?: ((user: AuthResponse, method: 'email' | 'phone') => void) | null;
    onVerify?: ((method: 'email' | 'phone') => void) | null;
    onCancelVerify?: (() => void) | null;
    onTypeChange?: ((newType: 'email' | 'tel') => void) | null;
    fieldClassName?: string | null;
    className?: string | null;
}

function LoginForm({
    onVerify = null,
    onCancelVerify = null,
    onComplete = null,
    onTypeChange = null,
    fieldClassName = null,
    className = null,
}: LoginFormProps) {
    const intl = useIntl();
    const { sendTokenAsync } = useAuthSendToken();
    const { loginAsync } = useAuthLogin();
    const tracking = useTracking();
    const formFields = ['field', 'phone', 'email'];

    const [fieldType, setFieldType] = useState<'email' | 'tel'>('email');
    const [verifyToken, setVerifyToken] = useState(false);
    const method = fieldType === 'tel' ? 'phone' : 'email';

    const postSendToken = (formAction: string, { field }: { field: string }) =>
        sendTokenAsync({
            [method]: field,
        });
    const onSendTokenComplete = () => {
        setVerifyToken(true);
        if (onVerify !== null) {
            onVerify(method);
        }
        tracking.trackEvent('Auth', 'send_token', method);
    };

    const { fields, onSubmit, status, setValue, errors, setErrors } = useForm({
        fields: formFields,
        postForm: postSendToken,
        onComplete: onSendTokenComplete,
    });

    const {
        field: { value: fieldValue },
    } = fields;

    const onFieldTypeChange = (newType: 'email' | 'tel') => {
        setFieldType(newType);
        setErrors(null);
        if (onTypeChange !== null) {
            onTypeChange(newType);
        }
    };

    const postVerifyToken = (formAction: string, { token }: { token: string }) =>
        loginAsync({
            [method]: fieldValue,
            token,
        });

    const onClickCancelVerify = () => {
        setVerifyToken(false);
        setValue({
            field: null,
        });
        if (onCancelVerify !== null) {
            onCancelVerify();
        }
        tracking.trackEvent('Auth', 'cancel_verify', method);
    };

    const onVerifyTokenComplete = (newUser: AuthResponse) => {
        if (onComplete !== null) {
            onComplete(newUser, method);
        }
        tracking.trackEvent('Auth', 'login', method);
    };

    const { errors: fieldErrors = null } = fields[method] || {};

    if (verifyToken) {
        return (
            <VerifyToken
                postForm={postVerifyToken}
                method={method}
                autoFocus
                withoutDescription
                className={classNames([styles.container, className])}
                onClickCancel={onClickCancelVerify}
                onComplete={onVerifyTokenComplete}
            />
        );
    }
    return (
        <form
            className={classNames([
                styles.container,
                {
                    'was-validated': errors !== null,
                },
                className,
            ])}
            onSubmit={onSubmit}
        >
            <FormControl {...fields[method]} withoutErrors>
                <div className={styles.fieldGroup}>
                    <ContactField
                        {...fields.field}
                        required
                        withBorder
                        errors={fieldErrors}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Inscris ton n. de tel ou courriel',
                            description: 'Field placeholder',
                        })}
                        onTypeChange={onFieldTypeChange}
                        className={fieldClassName}
                    />
                    <FormButton
                        type="submit"
                        transparent
                        className={styles.button}
                        disabled={status === 'loading'}
                    >
                        {status === 'loading' ? (
                            <LoadingIcon className={styles.icon} />
                        ) : (
                            <FieldArrow className={styles.icon} />
                        )}
                    </FormButton>
                </div>
            </FormControl>
        </form>
    );
}

export default LoginForm;
